import {
  type ConsentType,
  type Customer,
  type CustomerConsent as CustomerConsentType,
  type hfb,
  triggerConsentMessage,
  useConsent,
} from "@repo/api";
import {
  Accordion,
  AccordionItem,
  Button,
  Checkbox,
  InlineMessage,
  type InlineMessageVariant,
  RadioButtonGroup,
  Text,
  TextArea,
  mailIcon,
  mobileHandoverIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import { getConsentStatus, isDefined, trackAnalytics } from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import { useState } from "react";

import { CustomerCardWrapper } from "../../components/CustomerCardWrapper";
import { ElementsContainer } from "../../components/ElementsContainer";
import { SectionContainer } from "../../components/SectionContainer";
import { useToast } from "../../components/Toast";
import { useSettings } from "../../settings/SettingsProvider";
import { getConsentValue } from "../../utils/consentHelpers";
import { translateConsentMessageResult } from "../../utils/translationHelpers";
import { changeManualConsent } from "./changeManualConsent";
import { getConsentDescription } from "./getConsentDescription";
import { getConsentMessage } from "./getConsentMessage";

interface CustomerConsentProps {
  consentTypes: ConsentType[];
  customer?: Customer;
  manualConsents?: CustomerConsentType[] | undefined;
  setManualConsents?: (consents: CustomerConsentType[]) => void;
  opportunityId?: number;
  departments?: hfb[];
  setReason?: (reason: string) => void;
  isFromCustomerSettings?: boolean;
}

export const CustomerConsent = ({
  opportunityId,
  consentTypes,
  customer,
  manualConsents, // Manual consent override
  setManualConsents,
  departments,
  setReason,
  isFromCustomerSettings,
}: CustomerConsentProps) => {
  const { updateToast } = useToast();
  const { consent_requirements } = useSettings();
  const { data: consentData } = useConsent(customer?.partyId, true);

  const [isReasonOpen, setIsReasonOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<"EMAIL" | "MOBILE" | undefined>();

  const multipleConsents = consentTypes.length > 1;
  const manualConsentType =
    consentTypes.length === 1 ? consentTypes[0] : "FOLLOWUP_CONSENT";
  const defaultManualConsent = manualConsents?.find(
    (el) => el.consentType === manualConsentType
  );

  const consentValues = consentTypes
    .map((consentType) =>
      getConsentValue(
        consentType,
        consentData?.customerConsents,
        departments,
        opportunityId
      )
    )
    .filter(isDefined)
    .map((value) => ({ ...value, status: getConsentStatus(value) }));

  const description = getConsentDescription(consentTypes);
  const consentMessage = getConsentMessage(consentValues);
  const resend =
    consentMessage?.type === "PENDING" || consentMessage?.type === "ERROR";

  const sendConsentApproval = async (contactType: "EMAIL" | "MOBILE") => {
    const contactDetail =
      contactType === "EMAIL" ? customer?.email : customer?.phoneNumber;
    if (customer) {
      setLoading(contactType);
      try {
        await triggerConsentMessage(
          customer,
          contactType,
          consentTypes,
          departments,
          opportunityId
        );

        updateToast({
          title: translateConsentMessageResult(contactType, true),
          message: translateConsentMessageResult(
            contactType,
            true,
            true,
            contactDetail
          ),
        });
      } catch {
        updateToast({
          title: translateConsentMessageResult(contactType, false),
          message: translateConsentMessageResult(
            contactType,
            false,
            true,
            contactDetail
          ),
        });
      } finally {
        setLoading(undefined);
      }
    }
  };

  const onChangeManualConsent = (value: boolean, key?: ConsentType) => {
    if (setReason) {
      setIsReasonOpen(true);
    }

    const consentType = key ?? manualConsentType;

    const consentFromApi = getConsentValue(
      consentType,
      consentData?.customerConsents,
      departments,
      opportunityId
    );

    const newConsents =
      changeManualConsent(value, consentType, manualConsents, consentFromApi) ??
      [];

    // add default promotional consent if it's missing
    if (
      consent_requirements?.promotional_consent &&
      newConsents?.find((el) => el.consentType === "PROMOTIONAL_CONSENT") ===
        undefined &&
      !isFromCustomerSettings
    ) {
      newConsents.push({
        consentType: "PROMOTIONAL_CONSENT",
        consentProvided: false,
      });
    }

    setManualConsents?.(newConsents);
  };

  if (!customer) {
    return <Text>{t("prompt.missing_customer_details")}</Text>;
  }

  if (isEmpty(departments)) {
    return <Text>{t("prompt.missing_department_details")}</Text>;
  }

  return (
    <SectionContainer>
      {description["desc_1"] && <Text>{description["desc_1"]}</Text>}
      {description["desc_2"] && <Text>{description["desc_2"]}</Text>}

      <Accordion>
        {consent_requirements?.send_consent_approval && (
          <AccordionItem
            id="send-consent-request"
            title={t("action.send_consent_request")}
            open={true}
          >
            <SectionContainer>
              <InlineMessage
                title={consentMessage?.title}
                body={consentMessage?.body}
                variant={consentMessage?.variant as InlineMessageVariant}
              />
              <CustomerCardWrapper editable={false} projectId={opportunityId} />
              {consentMessage?.type !== "APPROVED" && (
                <>
                  <Button
                    size="small"
                    text={
                      resend ? t("action.resend_email") : t("action.send_email")
                    }
                    ssrIcon={mailIcon}
                    // TODO: revisit and fix

                    onClick={() => sendConsentApproval("EMAIL")}
                    loading={loading === "EMAIL"}
                    disabled={!customer || isEmpty(departments)}
                  />
                  <Button
                    size="small"
                    text={
                      resend ? t("action.resend_sms") : t("action.send_sms")
                    }
                    ssrIcon={mobileHandoverIcon}
                    // TODO: revisit and fix

                    onClick={() => sendConsentApproval("MOBILE")}
                    loading={loading === "MOBILE"}
                    disabled={!customer || isEmpty(departments)}
                  />
                </>
              )}
            </SectionContainer>
          </AccordionItem>
        )}

        {!consent_requirements?.disable_manual_consent && (
          <AccordionItem
            id="manual-consent"
            title={t("action.record_consent_manually")}
            open={!consent_requirements?.send_consent_approval}
          >
            <ElementsContainer>
              {consent_requirements?.enable_do_not_contact ? (
                <RadioButtonGroup
                  subtle
                  list={[
                    {
                      id: "manual-consent-no",
                      value: "false",
                      label: t("prompt.do_not_contact"),
                      defaultChecked:
                        defaultManualConsent?.consentProvided === false,
                      onChange: () => onChangeManualConsent(false),
                    },
                  ]}
                />
              ) : (
                <RadioButtonGroup
                  subtle
                  list={[
                    {
                      id: "manual-consent-yes",
                      value: "true",
                      label: t("prompt.agreed_to_be_contacted"),
                      defaultChecked:
                        defaultManualConsent?.consentProvided === true,
                      onChange: () => onChangeManualConsent(true),
                    },
                    {
                      id: "manual-consent-no",
                      value: "false",
                      label: t("prompt.do_not_contact"),
                      defaultChecked:
                        defaultManualConsent?.consentProvided === false,
                      onChange: () => onChangeManualConsent(false),
                    },
                  ]}
                />
              )}

              {consent_requirements?.promotional_consent &&
                multipleConsents &&
                !consent_requirements?.enable_do_not_contact && (
                  <Checkbox
                    subtle
                    id="manaul-promotional-consent"
                    label={t("prompt.agree_promotional_consent")}
                    onChange={(e) =>
                      onChangeManualConsent(
                        e.target.checked,
                        "PROMOTIONAL_CONSENT"
                      )
                    }
                    checked={
                      manualConsents?.find(
                        (el) => el.consentType === "PROMOTIONAL_CONSENT"
                      )?.consentProvided
                    }
                    value="manual-promotional-consent"
                  />
                )}
              {isReasonOpen && (
                <TextArea
                  id="consent-reason"
                  label={t("prompt.change_consent_reason")}
                  maxLength={400}
                  onChange={(e) => {
                    if (setReason) {
                      setReason?.(e.target.value);
                    }
                    trackAnalytics("change_consent_reason");
                  }}
                />
              )}
            </ElementsContainer>
          </AccordionItem>
        )}
      </Accordion>
    </SectionContainer>
  );
};
